import React, { useRef, useState } from "react";
import { Form, message } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { v4 as uuid } from "uuid";
import { ReactSortable } from "react-sortablejs";
import {
    formConf,
    inputComponents,
    selectComponents,
    extraComponents,
    layoutComponents,
} from "../components/formConfig";
import { RightPanel } from "../components/right-panel";
import { FormItem } from "../components/form-item";
import { FooterButtons } from "../components/footer-buttons";
import { Icon } from "../components/icon";

// 每个formItem的占比
const ColSpan = 12;
// formItem内label占比
const labelColSpan = 6;

const FormGenerator = () => {
    const [form] = Form.useForm();

    // 控件配置
    const [formItems, setFormItems] = useState([]);
    // 表单配置
    const [formConfig, setFormConfig] = useState(formConf);
    // 当前选中的控件 选中可以编辑控件属性
    const [compInfo, setCompInfo] = useState(null);
    // 选中的控件父级 比如表格
    const curCompParent = useRef();

    // 表单控件数 递增
    const formItemIndex = useRef(0);

    // 左侧可选控件列表
    const componentList = [
        {
            title: "输入型组件",
            components: inputComponents,
        },
        {
            title: "选择型组件",
            components: selectComponents,
        },
        {
            title: "增强型组件",
            components: extraComponents,
        },
        {
            title: "布局型组件",
            components: layoutComponents,
        },
    ];

    // 创建或编辑控件元素
    const makeNewItem = (item, isEdit) => {
        formItemIndex.current++;

        // 增加一项，插入一个唯一key
        return {
            ...item,
            ...(isEdit ? {} : {
                key: uuid()
            }),
            formItemProps: {
                ...item.formItemProps,
                name: `field${formItemIndex.current}`,
                labelCol: { span: labelColSpan },
                wrapperCol: { span: 24 - labelColSpan },
            },
            // 控件通用属性
            span: item.type === 'table' ? 24 : ColSpan,
            showLabel: ['blank', 'text', 'button', 'table'].indexOf(item.type) >= 0 ? undefined : true,
        };
    }

    // 选中并添加控件
    const onItemClick = (item) => {
        const newComp = makeNewItem({
            ...item,
            ...(item.type === 'table' && item.children.length === 0 ? {
                children: makeTableTds(item.props.rows, item.props.cols)
            } : {})
        });
        setFormItems([...formItems, newComp]);

        // 选中
        setCompInfo(newComp);
    };

    // 创建表格单元格
    const makeTableTds = (rowLen, colLen) => {
        const rows = [];
        for (let i = 0; i < (rowLen || 3); i++) {
            const cols = [];
            for (let j = 0; j < (colLen || 4); j++) {
                cols.push({
                    // 列合并参数
                    colspan: 1,
                    // 行合并参数
                    rowspan: 1,
                    // 能否向右合并 初始状态
                    rightDisabled: j === colLen - 1,
                    // 能否向下合并 初始状态
                    downDisabled: i === rowLen - 1,
                    // 拆分列
                    splitColumnDisabled: true,
                    // 拆分行
                    splitRowDisabled: true,
                    // 删除列
                    deleteColumnDisabled: false,
                    // 删除行
                    deleteRowDisabled: false,
                    // 单元格内控件列表
                    children: [],
                })
            }
            rows.push(cols);
        }
        return rows;
    }

    // 一维控件sort setList
    const onCompItemChange = (list) => {
        // 从左侧控件库拖入
        const hasInitData = list.find((item) => !item.key);
        if (hasInitData) {
            const newList = list.map((item) => {
                if (!item.key) {
                    return makeNewItem({
                        ...item,
                        ...(item.type === 'table' && item.children.length === 0 ? {
                            children: makeTableTds(item.props.rows, item.props.cols)
                        } : {})
                    });
                }

                return item;
            })
            setFormItems(newList);
            return;
        }

        // 从单元格移出
        const isMoveOutTd = list.find((item) => (item.formItemProps.name || '').includes('_td_'));
        if (isMoveOutTd) {
            const [tableKey, , trI, tdI] = isMoveOutTd.formItemProps.name.split('_');

            const newList = list.map((item) => {
                // 移出的控件 更新name和showLabel
                if (item.key === isMoveOutTd.key) {
                    return makeNewItem(item, true);
                }

                // 移除原表格单元格内容
                if (item.formItemProps.name === tableKey) {
                    return {
                        ...item,
                        children: item.children.map((tr, rIndex) => {
                            if (rIndex !== Number(trI)) return tr;
                            return tr.map((td, cIndex) => {
                                if (cIndex !== Number(tdI)) return td;

                                return {
                                    ...td,
                                    children: td.children.filter(el => el.key !== isMoveOutTd.key)
                                }
                            });
                        })
                    };
                }
                return item;
            });

            setTimeout(() => {
                setFormItems(newList);
            }, 0);
            return;
        }
        // 被移入单元格 表格不可再移入单元格
        if (list.length < formItems.length) {
            const newItemKeys = list.map(({ key }) => key);
            const moveOne = formItems.find(({ key }) => newItemKeys.indexOf(key) < 0);
            if (moveOne && moveOne.type === 'table') {
                return;
            }
        }
        setFormItems(list);
    };

    // 复制控件 注意要使用新的key
    const onItemCopy = (item) => {
        formItemIndex.current++;

        const copyComp = {
            ...item,
            key: uuid(),
            formItemProps: {
                ...item.formItemProps,
                name: `field${formItemIndex.current}`,
            },
            // 克隆表格时，单元格内的控件field name需要修改
            ...(
                item.type === 'table' ? {
                    children: item.children.map((tr, r_i) => {
                        return tr.map((td, c_i) => {
                            return {
                                ...td,
                                children: td.children.map((cItem, i) => {
                                    return {
                                        ...cItem,
                                        formItemProps: {
                                            ...cItem.formItemProps,
                                            name: `field${formItemIndex.current}_td_${r_i}_${c_i}_${i}`,
                                        },
                                    };
                                })
                            };
                        });
                    })
                } : {}
            )
        };
        setFormItems([...formItems, copyComp]);
    };

    // 移除控件
    const onItemDelete = (item) => {
        setFormItems(formItems.filter((el) => el.key !== item.key));

        // 如果删除的是当前高亮，则清空控件信息
        if (compInfo && compInfo.key === item.key) {
            setCompInfo(null);
        }
    };

    // 更新表单属性 type: formItemProps | props | null
    const updateCompProps = (type, newProps) => {
        const getUpdateObj = (item) => {
            if (type === "formItemProps") {
                return {
                    formItemProps: {
                        ...item.formItemProps,
                        ...newProps,
                    },
                };
            } else if (type === "props") {
                return {
                    props: {
                        ...item.props,
                        ...newProps,
                    },
                };
            } else {
                return newProps;
            }
        };

        // 更新列表 更新单元格内控件 或 一维控件
        if (curCompParent.current) {
            const { pKey, rIndex, cIndex, index } = curCompParent.current;
            setFormItems(
                formItems.map((item) => {
                    if (item.key !== pKey) return item;

                    return {
                        ...item,
                        children: item.children.map((tr, i_c) => {
                            if (i_c !== rIndex) return tr;
                            return tr.map((td, i_c) => {
                                if (i_c !== cIndex) return td;
                                return {
                                    ...td,
                                    children: td.children.map((cItem, comp_index) => {
                                        if (comp_index !== index) return cItem;
                                        return {
                                            ...cItem,
                                            ...getUpdateObj(cItem)
                                        };
                                    })
                                };
                            });
                        })
                    };
                })
            );
        } else {
            setFormItems(
                formItems.map((item) => {
                    if (item.key !== compInfo.key) return item;

                    return {
                        ...item,
                        ...getUpdateObj(item),
                    };
                })
            );
        }

        // 更新当前
        setCompInfo({
            ...compInfo,
            ...getUpdateObj(compInfo),
        });

        // 设置初始值 可视化
        if (type === "formItemProps" && newProps.initialValue !== undefined) {
            form.setFieldsValue({
                [compInfo.formItemProps.name]: newProps.initialValue,
            });
        }
    };

    // 保存表单 要区分是新增还是编辑
    const onFormSave = async () => {
        // console.log({
        //   formConfig,
        //   formItems,
        // });

        if (formConfig.name.trim() === "") return message.warning("请输入表单名称");
        if (formItems.length === 0) return message.warning("请完善表单");

        // 判断表单元素字段名是否重复
        const allFormItemsName = formItems.map((item) => item.formItemProps.name);
        const allFormItemsUniq = [...new Set(allFormItemsName)];
        if (allFormItemsName.length > allFormItemsUniq.length)
            return message.warning("组件字段名不能重复，请检查各组件");

        // 忽略掉空的校验规则，否则表单校验时该表单元素必须为空才通过
        const data = {
            formConfig,
            formItems: formItems.map((item) => ({
                ...item,
                formItemProps: {
                    ...item.formItemProps,
                    ...(item.formItemProps.rules
                        ? {
                            rules: (item.formItemProps.rules || []).filter(
                                (rule) =>
                                    rule.required !== undefined ||
                                    (rule.reg && rule.reg.trim() !== "" && rule.message)
                            ),
                        }
                        : {}),
                },
            })),
        };

        const vals = await form.validateFields();
        console.log(data, vals);
    };
    // console.log('formItems', formItems);

    return (
        <Container>
            <div className="form-generator-container">
                <ComponentList>
                    {componentList.map((item, index) => (
                        <div className="comp-types" key={index}>
                            <div>
                                <Icon type="component" />
                                {item.title}
                            </div>
                            <div className="components">
                                <ReactSortable
                                    className="comp-source ant-row"
                                    group={{
                                        name: "comp",
                                        pull: "clone",
                                        put: false,
                                    }}
                                    draggable=".component-item"
                                    animation={150}
                                    sort={false}
                                    list={item.components}
                                    setList={(e) => { }}
                                >
                                    {item.components.map((comp, cindex) => (
                                        <div
                                            key={cindex}
                                            className="component-item"
                                            onClick={() => onItemClick(comp)}
                                        >
                                            <Icon type={comp.type} />
                                            <span>{comp.formItemProps.label}</span>
                                        </div>
                                    ))}
                                </ReactSortable>
                            </div>
                        </div>
                    ))}
                </ComponentList>
                <FormLayout>
                    {formItems.length === 0 ? (
                        <div className="empty-tips">从左侧拖入或点击组件进行表单设计</div>
                    ) : null}
                    <Form {...formConfig} form={form}>
                        <ReactSortable
                            className="comp-item ant-row drop-holder"
                            group="comp"
                            animation={250}
                            list={formItems}
                            setList={onCompItemChange}
                            fallbackOnBody={true}
                        >
                            {formItems.map((item, index) => (
                                <FormItem
                                    key={index}
                                    formConfig={formConfig}
                                    item={item}
                                    activeKey={compInfo ? compInfo.key : ""}
                                    onClick={(compItem, pItem) => {
                                        setCompInfo(compItem);
                                        curCompParent.current = pItem;
                                    }}
                                    formItems={formItems}
                                    setFormItems={setFormItems}
                                    generate
                                >
                                    <div
                                        className="icon-btn copy"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onItemCopy(item);
                                        }}
                                    >
                                        <CopyOutlined />
                                    </div>
                                    <div
                                        className="icon-btn delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onItemDelete(item);
                                        }}
                                    >
                                        <DeleteOutlined />
                                    </div>
                                </FormItem>
                            ))}
                        </ReactSortable>
                    </Form>
                </FormLayout>
                <RightPanel
                    compInfo={compInfo}
                    formConfig={formConfig}
                    updateCompProps={updateCompProps}
                    setFormConfig={setFormConfig}
                />
            </div>
            <FooterButtons onClose={() => { }} onSave={onFormSave} />
        </Container>
    );
};

export default FormGenerator;

const Container = styled.div`
    .form-generator-container {
        display: flex;
        height: calc(100vh - 278px);
        position: relative;
        overflow: hidden;
        padding: 0 10px;
        border: 1px solid #e8e8e8;
    }
`;

const ComponentList = styled.div`
    width: 230px;
    border-right: 1px solid #e8e8e8;
    height: 100%;
    .comp-types {
        padding: 10px 0;
    }
    .components {
        display: flex;
        flex-wrap: wrap;
        .component-item {
            width: 100px;
            border: 1px solid #cecece;
            background-color: #fafafa;
            border-radius: 3px;
            padding: 4px 10px;
            margin: 5px 10px 5px 0;
            cursor: move;
        }
    }
`;

const FormLayout = styled.div`
    height: 80vh;
    flex: 1;
    padding: 10px 10px 0;
    position: relative;
    overflow: auto;
    .drop-holder {
        min-height: 76vh;
        align-content: flex-start;
    }
    .empty-tips {
        position: absolute;
        left: 0;
        top: 50%;
        width: 100%;
        text-align: center;
        font-size: 16px;
        color: #ccc;
    }
    .icon-btn {
        position: absolute;
        z-index: 2;
        top: -10px;
        width: 22px;
        height: 22px;
        line-height: 18px;
        text-align: center;
        border-radius: 50%;
        font-size: 12px;
        border: 1px solid #555;
        background: #fff;
        cursor: pointer;
        display: none;
        &.copy {
            right: 50px;
            border-color: #409eff;
            color: #409eff;
        }
        &.delete {
            right: 20px;
            border-color: #f56c6c;
            color: #f56c6c;
        }
    }
    .col-item {
        position: relative;
        padding: 5px;
        &:hover > .icon-btn {
            display: block;
            &.copy {
                background: #409eff;
                color: #fff;
            }
            &.delete {
                background: #f56c6c;
                color: #fff;
            }
        }
        &.active > .ant-form-item {
            background: rgba(40, 120, 255, 0.1);
        }
    }
    .ant-form-item {
        padding: 6px;
        border: 1px dashed #e8e8e8;
        border-radius: 2px;
        margin-bottom: 0;
        cursor: move;
        .ant-form-item-control {
            cursor: default;
        }
        &:hover {
            background: rgba(40, 120, 255, 0.1);
        }
    }
    .component-item {
        border: 1px dashed #e8e8e8;
        background: rgba(40, 120, 255, 0.1);
        border-radius: 2px;
        padding: 0 10px;
        margin: 5px;
        display: flex;
        align-items: center;
        flex: 1;
    }
`
